import {
  Box,
  Paper,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckIcon from '@mui/icons-material/Check';
import { Button } from 'system/atoms';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useMutation, useQuery, useSubscriber } from 'services/api/hooks';
import { getUnansweredQuestionsByConversation } from 'services/conversations/api';
import { UnansweredQuestion } from 'services/conversations/types';
import { updateUnansweredQuestion } from 'services/conversations/api';
import { onCreateUnansweredQuestion } from 'services/conversations/subscriptions';
import { useFormik } from 'formik';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { Accordion } from 'common/ui';
import { Checkbox } from 'common/form';
import { useUpdateConversationMutation } from 'generated-graphql/hooks/apollo-hooks';
import { UnresolvedNeedsReviewReason } from 'modules/conversations/constants/enums';
import { isConversationCompleted } from 'modules/conversations/utils/isConversationCompleted';
import {
  getKnownNeedsReviewLines,
  getNeedsReviewPanelLines
} from 'modules/conversations/utils/needsReviewReasonCopy';
import { ConversationStatus } from 'generated-graphql/types';

const questionItemPaper: SxProps<Theme> = {
  backgroundColor: 'customGrey.light',
  border: (theme) => theme.palette.border.small.grey.medium
};

const clickableQuestionItem: SxProps<Theme> = {
  ...questionItemPaper,
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: 'customGrey.medium'
  }
};

const resolvedQuestionItem: SxProps<Theme> = {
  backgroundColor: 'customGrey.light',
  border: (theme) => theme.palette.border.small.grey.medium
};

const accordionBorderStyle: SxProps<Theme> = {
  borderTop: (theme) => theme.palette.border.small.warning.dark,
  borderBottom: (theme) => theme.palette.border.small.warning.dark
};

const checkboxSx: SxProps = {
  '& .MuiCheckbox-root': {
    transform: 'scale(0.64)',
    transformOrigin: 'left center'
  },
  '& .MuiTypography-root': {
    fontSize: '16px',
    fontWeight: 600,
    marginLeft: '4px'
  }
};

const resolvedTextSx: SxProps<Theme> = {
  textDecoration: 'line-through'
};

const warningIconSx: SxProps<Theme> = {
  fontSize: '20px',
  color: 'warningV2.dark'
};

const successIconSx: SxProps<Theme> = {
  fontSize: '20px',
  color: 'successV2.main'
};

const resolveAllButtonSx = (isVisible: boolean): SxProps<Theme> => ({
  p: 0,
  alignSelf: 'flex-start',
  textDecoration: 'none',
  fontWeight: 600,
  opacity: isVisible ? 1 : 0,
  maxHeight: isVisible ? '40px' : 0,
  overflow: 'hidden',
  transition: 'opacity 0.3s ease, max-height 0.3s ease',
  pointerEvents: isVisible ? 'auto' : 'none',
  '&:hover': {
    textDecoration: 'none'
  }
});

const getUnresolvedMessageIds = (questions: UnansweredQuestion[]): string[] => {
  return questions
    .filter(
      (q): q is UnansweredQuestion & { associatedMessageId: string } =>
        !!q.associatedMessageId && !q.isAnswered
    )
    .map((q) => q.associatedMessageId);
};

type Props = {
  conversationId: string;
  formik: ReturnType<typeof useFormik<{ messageBody: string }>>;
  updateConversationHandler: (type: 'user' | 'agent') => void;
  isHumanTakeOver: boolean;
  isDifferentConversationOngoing: boolean;
  conversationStatus: ConversationStatus;
  needsReview: boolean;
  unresolvedNeedsReviewReasons?: string[] | null;
  onScrollToMessage?: (messageId: string) => void;
  onQuestionsLoaded?: (flaggedMessageIds: string[]) => void;
  onNeedsReviewChange?: (needsReview: boolean) => void;
};

const QuestionItem: React.FC<{
  item: UnansweredQuestion;
  onResolveQuestion: (id: string, isChecked: boolean) => void;
  onScrollToMessage?: (messageId: string) => void;
}> = ({ item, onResolveQuestion, onScrollToMessage }) => {
  const { track } = useTracking();

  const handleClick = () => {
    if (onScrollToMessage && item.associatedMessageId) {
      onScrollToMessage(item.associatedMessageId);
    }
  };

  const getPaperStyle = (): SxProps<Theme> => {
    if (item.isAnswered) {
      return resolvedQuestionItem;
    }
    if (onScrollToMessage) {
      return clickableQuestionItem;
    }
    return questionItemPaper;
  };

  const showTooltip = Boolean(onScrollToMessage && item.associatedMessageId);

  return (
    <Stack spacing={1}>
      <Tooltip title={showTooltip ? 'Show in conversation' : ''} arrow>
        <Paper variant="rounded" sx={getPaperStyle()} onClick={handleClick}>
          <Typography sx={item.isAnswered ? resolvedTextSx : undefined}>
            {item.summary}
          </Typography>
        </Paper>
      </Tooltip>
      <Checkbox
        value={item.isAnswered}
        onChange={(value) => {
          track(TrackingEventName.CampaignDashboardConversationResolve);
          onResolveQuestion(item.id, value);
        }}
        field={{
          checkboxLabel: 'Resolved',
          controlSx: checkboxSx
        }}
      />
    </Stack>
  );
};

export const UnansweredQuestions: React.FC<Props> = ({
  conversationId,
  isHumanTakeOver,
  conversationStatus,
  needsReview,
  unresolvedNeedsReviewReasons,
  onScrollToMessage,
  onQuestionsLoaded,
  onNeedsReviewChange
}) => {
  const [unansweredQuestions, setUnansweredQuestions] = useState<
    UnansweredQuestion[]
  >([]);
  const [keepIssuesOpen, setKeepIssuesOpen] = useState(needsReview);
  const hasInitialClearRun = useRef(false);

  // Sync keepIssuesOpen with needsReview prop when it changes (e.g., on refresh)
  useEffect(() => {
    setKeepIssuesOpen(needsReview);
  }, [needsReview]);

  const { isLoading, data } = useQuery<UnansweredQuestion[]>(
    getUnansweredQuestionsByConversation,
    {
      conversationId
    },
    {
      onFetchSuccess: (data) => {
        setUnansweredQuestions(data);
        if (onQuestionsLoaded) {
          onQuestionsLoaded(getUnresolvedMessageIds(data));
        }
      }
    }
  );

  const { mutate: updateQuestionMutation } = useMutation(
    updateUnansweredQuestion,
    { disableSuccessToast: true }
  );

  const [updateConversation] = useUpdateConversationMutation();

  const unresolvedCount = useMemo(
    () => unansweredQuestions.filter((question) => !question.isAnswered).length,
    [unansweredQuestions]
  );

  const sortedQuestions = useMemo(
    () =>
      [...unansweredQuestions].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      ),
    [unansweredQuestions]
  );

  const isSomeQuestionUnresolved = unresolvedCount > 0;

  const shouldClearNeedsReview =
    needsReview &&
    (isHumanTakeOver || isConversationCompleted(conversationStatus)) &&
    !isSomeQuestionUnresolved &&
    !keepIssuesOpen;

  useSubscriber({
    subQuery: onCreateUnansweredQuestion,
    subParams: {
      conversationId: { eq: conversationId }
    },
    onFetchSuccess: (data) => {
      const newQuestion: UnansweredQuestion = data?.onCreateUnansweredQuestion;

      if (newQuestion) {
        setUnansweredQuestions((prev) => {
          const updated = [...prev, newQuestion];
          if (onQuestionsLoaded) {
            onQuestionsLoaded(getUnresolvedMessageIds(updated));
          }
          return updated;
        });
      }
    }
  });

  const onResolveQuestion = async (id: string, isChecked: boolean) => {
    const question = await updateQuestionMutation({
      id,
      isAnswered: isChecked
    });

    const updatedQuestions = unansweredQuestions.map((item) =>
      item.id === question.id ? question : item
    );

    setUnansweredQuestions(updatedQuestions);

    if (onQuestionsLoaded) {
      onQuestionsLoaded(getUnresolvedMessageIds(updatedQuestions));
    }

    // If a question is being unresolved, set needs review to true
    if (!isChecked) {
      updateConversationToReview(true);
      return;
    }

    // If all questions are now resolved and "Keep issues open" is not checked, clear the needs review flag
    const allResolved = updatedQuestions.every((q) => q.isAnswered);
    if (allResolved && !keepIssuesOpen) {
      updateConversationToReview(false);
    }
  };

  const onResolveAllQuestions = async () => {
    await Promise.all(
      unansweredQuestions.map((question) =>
        updateQuestionMutation({ id: question.id, isAnswered: true })
      )
    );

    const updatedQuestions = unansweredQuestions.map((question) => ({
      ...question,
      isAnswered: true
    }));

    setUnansweredQuestions(updatedQuestions);

    if (onQuestionsLoaded) {
      onQuestionsLoaded(getUnresolvedMessageIds(updatedQuestions));
    }

    // If "Keep issues open" is not checked, clear the needs review flag
    if (!keepIssuesOpen) {
      updateConversationToReview(false);
    }
  };

  const updateConversationToReview = useCallback(
    async (newNeedsReview: boolean) => {
      await updateConversation({
        variables: {
          input: {
            id: conversationId,
            needsReview: newNeedsReview
          }
        }
      });
      onNeedsReviewChange?.(newNeedsReview);
    },
    [conversationId, onNeedsReviewChange, updateConversation]
  );

  // Auto-clear needsReview on initial load if conditions are met
  useEffect(() => {
    if (shouldClearNeedsReview && data && !hasInitialClearRun.current) {
      hasInitialClearRun.current = true;
      updateConversationToReview(false);
    }
  }, [data?.length, shouldClearNeedsReview, updateConversationToReview]);

  // Ensure needsReview is true when there are unresolved questions
  useEffect(() => {
    if (isSomeQuestionUnresolved && !needsReview) {
      updateConversationToReview(true);
    }
  }, [isSomeQuestionUnresolved, needsReview, updateConversationToReview]);

  if (isLoading) {
    return <Skeleton variant="rectangular" width="100%" height={50} />;
  }

  if (!unansweredQuestions.length && !needsReview) {
    return null;
  }

  const hasNoQuestionsButNeedsReview =
    !unansweredQuestions.length && needsReview;
  const showWarningState =
    isSomeQuestionUnresolved || keepIssuesOpen || hasNoQuestionsButNeedsReview;

  const getHeaderContent = () => (
    <Stack spacing={1.25} direction="row" alignItems="center">
      {showWarningState ? (
        <WarningAmberIcon sx={warningIconSx} />
      ) : (
        <CheckIcon sx={successIconSx} />
      )}
      <Typography>
        {isSomeQuestionUnresolved ? `Review (${unresolvedCount})` : 'Review'}
      </Typography>
    </Stack>
  );

  return (
    <Box
      sx={showWarningState ? accordionBorderStyle : undefined}
      data-accordion-border={showWarningState ? 'warning' : undefined}
    >
      <Accordion
        title={getHeaderContent()}
        backgroundColor={showWarningState ? 'warningV2.light' : undefined}
      >
        <Stack spacing={3}>
          {hasNoQuestionsButNeedsReview ? (
            <Stack spacing={1}>
              {getNeedsReviewPanelLines(unresolvedNeedsReviewReasons).map(
                (line) => (
                  <Typography key={line} variant="body2">
                    {line}
                  </Typography>
                )
              )}
            </Stack>
          ) : (
            <Stack spacing={1}>
              <Typography variant="body2">
                Takeover the conversation to reply to the candidate. Hand back
                when you&apos;re done.
              </Typography>
              {/* Other unresolved reasons still show alongside the question
                  list; UNANSWERED_QUESTION is omitted because the questions
                  below are that signal. */}
              {getKnownNeedsReviewLines(
                (unresolvedNeedsReviewReasons ?? []).filter(
                  (reason) =>
                    reason !== UnresolvedNeedsReviewReason.UNANSWERED_QUESTION
                )
              ).map((line) => (
                <Typography key={line} variant="body2">
                  {line}
                </Typography>
              ))}
            </Stack>
          )}

          {sortedQuestions.length > 0 && (
            <>
              <Stack spacing={3}>
                {sortedQuestions.map((item) => (
                  <QuestionItem
                    key={item.id}
                    item={item}
                    onResolveQuestion={onResolveQuestion}
                    onScrollToMessage={onScrollToMessage}
                  />
                ))}
              </Stack>

              <Button
                variant="link"
                sx={resolveAllButtonSx(isSomeQuestionUnresolved)}
                onClick={onResolveAllQuestions}
              >
                Resolve all
              </Button>
            </>
          )}

          <Checkbox
            value={keepIssuesOpen}
            onChange={(value) => {
              setKeepIssuesOpen(value);
              // Only call API when:
              // - Checking the box (value === true): always update to keep needsReview true
              // - Unchecking (value === false): only update if no unresolved questions
              //   (otherwise the useEffect will handle setting needsReview to true)
              if (value || !isSomeQuestionUnresolved) {
                updateConversationToReview(value);
              }
            }}
            field={{
              checkboxLabel: 'Keep "review" status',
              controlSx: checkboxSx
            }}
          />
        </Stack>
      </Accordion>
    </Box>
  );
};
